import {
  Component,
  Input,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogService } from '@nebular/theme';
import { LocalDataSource } from 'ng2-smart-table';
import { Subject } from 'rxjs/Subject';

import { FitnessPlanDay } from '../../../../@core/interfaces/common/CalorieFriend/plan-day';
import { format } from '../../../../../support';
import { BaseComponentCanDeactivate } from 'app/@auth/guards/changes.guard';
import { ViewChildren } from '@angular/core';
import { QueryList } from '@angular/core';
import { FormConstants } from '../../CustomUIOps';
import { isObservable } from 'rxjs';
import { CalculateBlockSummary, CopyFitnessItem } from 'app/BuildOps';
import { DayFitnessSummaryComponent } from '../FitnessSummary/fitness-summary.component';
import {
  BlockType,
  FitnessBlock,
  FitnessItem,
} from 'app/@core/interfaces/common/CalorieFriend/fitnessItem';
import { FitnessBlockComponent } from '../FitnessBlock/fitness-block.component';
import { ConfirmMessageDialogComponent } from 'app/@components/confirm-message-dialog/confirm-message-dialog.component';
import { takeUntil } from 'rxjs/operators';

/**
 * Base class for Plan Day components
 */
export class FitnessPlanDayComponentBase
  extends BaseComponentCanDeactivate
  implements OnInit, AfterViewInit, OnDestroy
{
  DayLabel = '';
  NewDay = $localize`:@@NewDay:New Day`;
  EditDay = $localize`:@@EditDay:Edit Day`;
  NameLabel = $localize`:@@Meal:Meal`;
  TimeLabel = $localize`:@@Time:Time`;
  BlockLabel = 'Block Name';

  readonly WeekDayofToday = new Date().getDay();

  PlanID = null;
  ShowSubmit = false;

  ParentPlanID: number = null;

  @Input('Day') Day: FitnessPlanDay;
  @Input('PlanDays') PlanDays: Array<FitnessPlanDay> = null;
  @Input('PlanView') PlanView: boolean = false;
  @Input('HidePlanName') HidePlanName: boolean = false;

  @Input('PatientID') PatientID: number = -1;
  @Input('PatientName') PatientName: string = '';
  @Input('IsPreferredUnitImperial') IsPreferredUnitImperial: boolean = false;

  DayForm: FormGroup;
  BlocksSource: LocalDataSource;

  DayExercises = 0;
  DayReps = 0;
  DayMinutes = 0;

  NewDayView = false;
  isDirty = false;

  @ViewChildren('FitnessBlock')
  private FitnessBlocks: QueryList<FitnessBlockComponent>;
  @ViewChild('DayFitnessSummaryView')
  DayFitnessSummaryView: DayFitnessSummaryComponent = null;

  DayNotesWidth = '80%';
  DayNotesHeight = '200px';

  FormConstants = FormConstants;

  /**
   * Settings of the ng2 smart table of Meals
   */
  MealTableSettings = {
    mode: 'external',
    actions: {
      position: 'right',
      add: false,
    },
    edit: {
      editButtonContent:
        '<img src="assets/icons/added_oultine_modify_pencil.svg" class="img-archive" width="24" height="24">',
    },
    delete: {
      deleteButtonContent:
        '<img src="assets/icons/trash-2-outline.svg" class="img-archive" width="24" height="24">',
    },
    columns: {
      name: { title: this.NameLabel, type: 'string', filter: false },
      time: { title: this.TimeLabel, type: 'string', filter: false },
    },
  };

  protected readonly unsubscribe$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private dialogService: NbDialogService,
    private chdetRef: ChangeDetectorRef
  ) {
    super(dialogService);
  }

  get DayName() {
    return this.DayForm.get('DayName');
  }
  get DayNotes() {
    return this.DayForm.get('DayNotes');
  }
  get assignWeekDays() {
    return this.DayForm.get('assignWeekDays');
  }

  get IsDirty() {
    let childrenDirty = false;
    this.FitnessBlocks.forEach(
      (c) => (childrenDirty = childrenDirty || c.IsDirty)
    );
    return (
      this.isDirty ||
      childrenDirty ||
      (this.DayFitnessSummaryView != null && this.DayFitnessSummaryView.IsDirty)
    );
  }

  resetDirty() {
    super.resetDirty();
    this.DayFitnessSummaryView.resetDirty();
    this.FitnessBlocks.forEach((c) => c.resetDirty());
  }

  /**
   * Initializes the component.
   * - Initializes the Day form.
   * - Gets the Plan ID from the URL
   * - Initalizes the Meals data source
   */
  ngOnInit(): void {
    // this.MealsOrdered();
    this.InitDayForm();

    if (!this.Day) {
      this.Day = {
        id: null,
        blocks: new Array<FitnessBlock>(),
        name: '',
        planID: this.ParentPlanID,
        notes: '',
        dayOfWeek: [],
        createMeal: false,
        order: 1,
      };

      this.ShowSubmit = true;
    } else {
      this.DayForm.patchValue({
        DayName: this.Day.name,
        DayNotes: this.Day.notes,
        assignWeekDays: this.Day.dayOfWeek,
      });

      this.ShowSubmit = false;
    }

    this.BlocksSource = new LocalDataSource(this.Day.blocks); // create the source

    this.PlanID = this.route.snapshot.paramMap.get('PlanId');

    if (this.Day.id !== null && this.Day.id > 0) {
      this.DayLabel = this.EditDay;
    } else {
      this.DayLabel = this.NewDay;
      this.NewDayView = true;
    }

    if (
      this.Day != null &&
      this.Day !== undefined &&
      this.Day.createMeal &&
      this.Day.blocks.length === 0
    ) {
      this.NewBlock();
    }

    this.PrepareDayObservables();
    this.PrepareMetricsTotalDayMetrics();
    super.resetDirty();

    this.OrderBlocks();

    // this.chdetRef.detectChanges();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public showWarmup(block: FitnessBlock) {
    if (
      block.blockType.toString() === BlockType.Warmup.toString() ||
      this.Day.blocks.filter(
        (y) => y.blockType.toString() === BlockType.Warmup.toString()
      ).length === 0
    ) {
      return true;
    }
    return false;
  }

  public showCooldown(block: FitnessBlock) {
    if (
      block.blockType.toString() === BlockType.Cooldown.toString() ||
      this.Day.blocks.filter(
        (y) => y.blockType.toString() === BlockType.Cooldown.toString()
      ).length === 0
    ) {
      return true;
    }
    return false;
  }

  /**
   * AfterViewInit implementation.
   * Updates the Metrics Graph for the day whenever view updates.
   */
  ngAfterViewInit() {
    this.updateFitnessSummaryUpdateBinding();
  }

  updateFitnessSummaryUpdateBinding() {
    if (this.FitnessBlocks) {
      this.FitnessBlocks.forEach((view) => {
        view.ItemUpdateNotification
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((data) => {
            this.PrepareDayObservables();
            this.DayFitnessSummaryView.PrepareBlockItems();
            this.PrepareMetricsTotalDayMetrics();
            this.OrderBlocks();

            this.chdetRef.detectChanges();
          });
      });
    }
  }

  /**
   * Method to initialize the Day form.
   */
  InitDayForm() {
    this.DayForm = this.fb.group({
      DayName: this.fb.control('', [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(50),
      ]),
      DayNotes: this.fb.control('', [Validators.maxLength(500)]),
      assignWeekDays: this.fb.control([]),
    });
    // this.initForm(this.DayForm);
  }

  /**
   * Method to open a New Meal component as a pop up window to add a new meal
   */
  NewBlock() {
    const orders = this.Day.blocks?.map((b) => b.order);
    const NewBlock: FitnessBlock = {
      id: -1,
      dayID: this.Day.id,
      name: format('{0}', this.BlockLabel),
      blockType: BlockType.Block,
      repetitionCount: 1,
      circuitRest: 0,
      restEnd: 0,
      items: null,
      order: Math.max(...(orders? orders: [0])) + 1,
    };

    this.UpdateBlock(NewBlock);
    this.OrderBlocks();
    this.chdetRef.detectChanges();
    this.updateFitnessSummaryUpdateBinding();
  }

  /**
   * Function to update the Day's block list with the {block} data.
   * @usageNotes
   * It is called after adding a new block or editing an existing one to update the blocks list.
   * @param block Meal object
   */
  UpdateBlock(block: FitnessBlock) {
    if (block !== null && block !== undefined) {
      // check if meal is in list --
      const found = this.Day.blocks?.find((element) => element === block);
      if (!found) {
        if(!this.Day.blocks) this.Day.blocks = [block];
        else this.Day.blocks.push(block);
      }

      if (this.DayFitnessSummaryView)
        this.DayFitnessSummaryView.PrepareBlockItems();
      this.PrepareMealItemsObservables(block);
      this.PrepareMetricsTotalDayMetrics();

      this.BlocksSource.refresh();

      this.isDirty = true;
    }
  }

  /**
   * Event handler of 'Edit' action.
   * Opens the Meal component to edit an existing meal and updates the meals list.
   * @param $event Event object
   */
  onEdit($event) {}

  /**
   * Removes the selected meal
   */
  RemoveBlock(block) {
    if (block.items.length === 0) {
      this.removeBlockCore(block);
      return;
    }

    this.dialogService
      .open(ConfirmMessageDialogComponent, {
        closeOnEsc: true,
        closeOnBackdropClick: true,
        context: { Message: 'Are you sure to delete this block?' },
      })
      .onClose.subscribe((result) => {
        if (result) {
          this.removeBlockCore(block);
        }
      });
  }

  removeBlockCore(block) {
    this.Day.blocks = this.Day.blocks.filter((m) => m !== block);
    this.BlocksSource = new LocalDataSource(this.Day.blocks); // create the source

    this.PrepareMetricsTotalDayMetrics();

    this.isDirty = true;

    this.OrderBlocks();
  }

  /**
   * Checks if Meal can be removed from the Day
   */
  get CanRemoveBlock() {
    if (this.Day.blocks && this.Day.blocks && this.Day.blocks.length > 1)
      return true;
    return false;
  }

  OrderUp(block) {
    const useFilteredBlocks = this.Day.blocks.filter(
      (y) =>
        y.blockType.toString() === BlockType.Block.toString() ||
        y.blockType.toString() === BlockType.Circuit.toString()
    );
    const index = useFilteredBlocks.findIndex((b) => b.order === block.order);

    if (index > 0) {
      const prevBlock = useFilteredBlocks[index - 1];
      const tempOrder = prevBlock.order;
      prevBlock.order = block.order;
      block.order = tempOrder;
    }
    this.OrderBlocks();
  }

  OrderDown(block) {
    const useFilteredBlocks = this.Day.blocks.filter(
      (y) =>
        y.blockType.toString() === BlockType.Block.toString() ||
        y.blockType.toString() === BlockType.Circuit.toString()
    );
    const index = useFilteredBlocks.findIndex((b) => b.order === block.order);

    if (index < useFilteredBlocks.length - 1) {
      const nextBlock = useFilteredBlocks[index + 1];
      const tempOrder = nextBlock.order;
      nextBlock.order = block.order;
      block.order = tempOrder;
    }
    this.OrderBlocks();
  }

  Copy(block: FitnessBlock) {
    FitnessBlockComponent.clipboard = block;
  }

  Paste(block: FitnessBlock) {
    const list: FitnessItem[] = [];

    if (FitnessBlockComponent.clipboard != null) {
      FitnessBlockComponent.clipboard.items.forEach((item) => {
        const newItem = CopyFitnessItem(item);
        newItem.fitnessBlockID = block.id;
        list.push(newItem);
      });
    }

    this.FitnessBlocks.find(
      (y) => y.FitnessBlock.order === block.order
    ).FitnessItemsView.UpdateSelectedItem(list);
  }

  HasClipboard(): boolean {
    return FitnessBlockComponent.clipboard != null;
  }

  Accept() {}

  Back() {
    // this.ref.close();
  }

  /**
   * Method to sort the meals list by its time
   */
  OrderBlocks() {
    const filteredBlocks = this.Day.blocks?.filter(
      (y) =>
        y.blockType.toString() !== BlockType.Warmup.toString() &&
        y.blockType.toString() !== BlockType.Cooldown.toString()
    );

    filteredBlocks?.sort((a, b) => (a.order > b.order ? 1 : -1));
    let index = 0;

    filteredBlocks?.forEach((a) => {
      a.order = ++index;
    });

    this.Day.blocks?.sort((a, b) => (a.order > b.order ? 1 : -1));

    if (this.FitnessBlocks) {
      this.FitnessBlocks.forEach((b) => b.UpdateLabel());
    }
  }

  /**
   * Method to prepare observables of the meals list by preparing observables for individual meal items
   */
  PrepareDayObservables() {
    // for the meal items  -- configure an observable
    // place an observable on the object

    if (this.Day !== null && this.Day !== undefined) {
      if (
        this.Day.blocks !== null &&
        this.Day.blocks !== undefined &&
        this.Day.blocks.length > 0
      ) {
        for (const block of this.Day.blocks) {
          this.PrepareMealItemsObservables(block);
        }
      }
    }
  }

  /**
   * Method to prepare an observable of the {meal} object.
   * Creates a 'Update' sujbect to subscribe with the callback which update the Metrics Graph.
   * @param block Meal object
   */
  private PrepareMealItemsObservables(block) {
    if (block && block.items) {
      for (const item of block.items) {
        if (!isObservable(item.Update)) item.Update = new Subject();

        item.Update
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((data) => {
          this.PrepareMetricsTotalDayMetrics();
        });
      }
    }
  }

  /**
   * Method to update Metrics graph data.
   */
  PrepareMetricsTotalDayMetrics() {
    let exercises = 0;
    let reps = 0;
    let seconds = 0;

    if (this.Day !== null && this.Day !== undefined) {
      if (
        this.Day.blocks !== null &&
        this.Day.blocks !== undefined &&
        this.Day.blocks.length > 0
      ) {
        for (const block of this.Day.blocks) {
          const blockSummary = CalculateBlockSummary(block);
          exercises += blockSummary.Exercises;
          reps += blockSummary.TotalReps;
          seconds += blockSummary.TotalSeconds;
        }

        this.DayExercises = Math.round(+exercises);
        this.DayReps = Math.round(+reps);
        this.DayMinutes = Math.round(+seconds / 60);
      }
    }
  }

  /**
   * Event handler for Name field
   * @param $event Event object
   */
  NameChange($event) {
    this.Day.name = this.DayName.value;
  }

  /**
   * Event handler for Notes field
   * @param $event Event object
   */
  NotesChange($event) {
    this.Day.notes = $event.target.value;
  }

  onAssignWeekDaysChagne($event): void {
    if ($event.target) return;
    this.Day.dayOfWeek = $event;
  }

  ViewPlan() {}

  private MonitorMealChanges() {
    if (
      this.Day != null &&
      this.Day !== undefined &&
      this.Day.blocks != null &&
      this.Day.blocks !== undefined
    ) {
    }
  }
}

/**
 * Component for Plan Day View.
 * Inherits from the PlanDayComponentBase component.
 */
@Component({
  selector: 'app-fitness-plan-day',
  templateUrl: './fitness-plan-day.component.html',
  styleUrls: [
    './fitness-plan-day.component.scss',
    '../../global.component.scss',
  ],
})
export class FitnessPlanDayComponent extends FitnessPlanDayComponentBase {
  constructor(
    protected PlanDayRef: NbDialogService,
    PlanDayRoute: ActivatedRoute,
    PlanDayFB: FormBuilder,
    PlanDayDialogService: NbDialogService,
    cdRef: ChangeDetectorRef
  ) {
    super(PlanDayRoute, PlanDayFB, PlanDayDialogService, cdRef);
  }
}

/**
 * Component for Plan Day View.
 * Inherits from the PlanDayComponentBase component.
 */
@Component({
  selector: 'app-fitness-plan-day-mobile',
  templateUrl: './fitness-plan-day.component.html',
  styleUrls: [
    './fitness-plan-day.component.scss',
    '../../global.component.scss',
  ],
})
export class FitnessPlanDayMobieComponent extends FitnessPlanDayComponentBase {
  constructor(
    protected PlanDayMobieRef: NbDialogService,
    PlanDayMobieRoute: ActivatedRoute,
    PlanDayMobieFB: FormBuilder,
    PlanDayMobieDialogService: NbDialogService,
    cdRefMobie: ChangeDetectorRef
  ) {
    super(
      PlanDayMobieRoute,
      PlanDayMobieFB,
      PlanDayMobieDialogService,
      cdRefMobie
    );
    this.PlanView = true;
    this.HidePlanName = true;
  }
}
